import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeftIcon, AlertTriangleIcon, CreditCardIcon, CalendarIcon, ClockIcon, FileTextIcon, UploadIcon, CheckCircleIcon, XCircleIcon, DownloadIcon, TrashIcon, InfoIcon, HelpCircleIcon, XIcon as CloseIcon, ChevronLeftIcon, ChevronRightIcon, PenIcon, ChevronUpIcon, ChevronDownIcon, EyeIcon, MoreVerticalIcon, PinIcon, LinkIcon, QrCodeIcon, FileIcon, SmartphoneIcon, TerminalIcon, CopyIcon, CheckIcon } from 'lucide-react';
import { Dispute, DisputeStatus, DisputeStage, DisputeReason, EvidenceDocument } from '../types/Dispute';
import { getEvidenceRecommendation } from '../utils/evidenceRecommendations';
import { generateDisputes } from '../utils/disputes';
interface RelatedOrder {
  id: string;
  orderId: string;
  amount: number;
  currency: string;
  createdTime: string;
  status: string;
  matchingCriteria: number;
  totalCriteria: number;
}
interface MatchingCriteriaDetail {
  category: string;
  subtitle: string;
  items: {
    label: string;
    matched: boolean;
  }[];
}
export function DisputeDetail() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const location = useLocation();
  const [showEvidenceForm, setShowEvidenceForm] = useState(false);
  const [showAlert, setShowAlert] = useState(true);
  const [activeTab, setActiveTab] = useState<'payment' | 'related'>('payment');
  const [selectedOrder, setSelectedOrder] = useState<RelatedOrder | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showMatchingCriteriaTooltip, setShowMatchingCriteriaTooltip] = useState(false);
  const [showReasonTooltip, setShowReasonTooltip] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [showChallengeFlow, setShowChallengeFlow] = useState(false);
  const [challengeStep, setChallengeStep] = useState<'reason' | 'evidence' | 'order' | 'customer' | 'other' | 'review'>('reason');
  const [selectedChallengeReason, setSelectedChallengeReason] = useState('purchasing_history');
  const [selectedProductType, setSelectedProductType] = useState('');
  const [noEvidenceChecked, setNoEvidenceChecked] = useState(false);
  const [noOrderEvidenceChecked, setNoOrderEvidenceChecked] = useState(false);
  const [noCustomerEvidenceChecked, setNoCustomerEvidenceChecked] = useState(false);
  const [noOtherEvidenceChecked, setNoOtherEvidenceChecked] = useState(false);
  const [showNoEvidenceWarning, setShowNoEvidenceWarning] = useState(false);
  const [reviewConfirmChecked, setReviewConfirmChecked] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    challengeDetails: true,
    transactionDetails: true,
    otherEvidence: true
  });
  const [showChallengeDetails, setShowChallengeDetails] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  // State to track if dispute has been accepted
  const [isAccepted, setIsAccepted] = useState(false);
  // State to track if dispute has been challenged (separate from derived isChallenged)
  const [hasBeenChallenged, setHasBeenChallenged] = useState(false);
  // State for adding notes
  const [showAddNote, setShowAddNote] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [merchantNotes, setMerchantNotes] = useState<Array<{
    id: string;
    timestamp: string;
    description: string;
    status: 'note';
    author: string;
    isPinned?: boolean;
  }>>([]);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const [showNoteMenu, setShowNoteMenu] = useState<string | null>(null);
  const [copiedOrderId, setCopiedOrderId] = useState<string | null>(null);
  // Check if returning from challenge submission
  useEffect(() => {
    if (location.state?.challenged) {
      setHasBeenChallenged(true);
    }
  }, [location.state]);
  // Close note menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showNoteMenu) {
        const target = event.target as HTMLElement;
        if (!target.closest('.note-menu-container')) {
          setShowNoteMenu(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNoteMenu]);
  const rowsPerPage = 5;
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const handleChallengeClick = () => {
    navigate(`/disputes/${id}/challenge`);
  };
  const handleViewChallengeDetails = () => {
    setShowChallengeDetails(true);
  };
  const handleChallengeConfirm = () => {
    setShowChallengeModal(false);
    setShowChallengeFlow(true);
    setChallengeStep('reason');
  };
  const handleChallengeReasonNext = () => {
    setChallengeStep('evidence');
  };
  const handleEvidenceNext = () => {
    setChallengeStep('order');
  };
  const handleOrderNext = () => {
    setChallengeStep('customer');
  };
  const handleCustomerNext = () => {
    setChallengeStep('other');
  };
  const handleOtherNext = () => {
    // Check if user has provided no evidence at all
    if (noEvidenceChecked && noOrderEvidenceChecked && noCustomerEvidenceChecked && noOtherEvidenceChecked) {
      setShowNoEvidenceWarning(true);
    } else {
      setChallengeStep('review');
    }
  };
  const handleReviewBack = () => {
    setChallengeStep('other');
  };
  const handleCloseChallengeFlow = () => {
    setShowChallengeFlow(false);
    setChallengeStep('reason');
    setSelectedChallengeReason('purchasing_history');
    setSelectedProductType('');
    setNoEvidenceChecked(false);
    setNoOrderEvidenceChecked(false);
    setNoCustomerEvidenceChecked(false);
    setNoOtherEvidenceChecked(false);
    setShowNoEvidenceWarning(false);
    setReviewConfirmChecked(false);
  };
  const handleAcceptClick = () => {
    setShowAcceptModal(true);
  };
  const handleAcceptConfirm = () => {
    setIsAccepted(true);
    setShowAcceptModal(false);
  };
  const handleAddNote = () => {
    if (noteText.trim()) {
      const newNote = {
        id: `note-${Date.now()}`,
        timestamp: new Date().toISOString(),
        description: noteText.trim(),
        status: 'note' as const,
        author: 'Eng Han Ng',
        isPinned: false
      };
      setMerchantNotes([newNote, ...merchantNotes]);
      setNoteText('');
      setShowAddNote(false);
    }
  };
  const handleEditNote = (noteId: string) => {
    const note = merchantNotes.find(n => n.id === noteId);
    if (note) {
      setEditingNoteId(noteId);
      setEditingNoteText(note.description);
      setShowNoteMenu(null);
    }
  };
  const handleSaveEdit = (noteId: string) => {
    if (editingNoteText.trim()) {
      setMerchantNotes(merchantNotes.map(note => note.id === noteId ? {
        ...note,
        description: editingNoteText.trim()
      } : note));
      setEditingNoteId(null);
      setEditingNoteText('');
    }
  };
  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditingNoteText('');
  };
  const handleDeleteNote = (noteId: string) => {
    setMerchantNotes(merchantNotes.filter(note => note.id !== noteId));
    setShowNoteMenu(null);
  };
  const handlePinNote = (noteId: string) => {
    setMerchantNotes(merchantNotes.map(note => note.id === noteId ? {
      ...note,
      isPinned: !note.isPinned
    } : note));
    setShowNoteMenu(null);
  };
  const handleCopyOrderId = async (orderId: string) => {
    try {
      await navigator.clipboard.writeText(orderId);
      setCopiedOrderId(orderId);
      setTimeout(() => setCopiedOrderId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  const getFileFormatRequirements = () => {
    const cardBrand = dispute.cardBrand.toLowerCase();
    // Check for Amex
    if (cardBrand === 'amex') {
      return {
        formats: 'Supported formats: TIFF, PDF, JPEG, JPG, PNG, DOC, Maximum individual file size: 4MB, Maximum PDF pages: less than 20 pages, Maximum image size: Not more than 300 x 300 DPI',
        language: 'Evidence language: English',
        files: 'Supported files: TIFF, TIF, PDF, JPEG, JPG, DOC, PNG and maximum combined file sizes: 25MB across all files.'
      };
    }
    // Check for Mastercard
    if (cardBrand === 'mastercard') {
      return {
        formats: 'Accepted file formats: TIFF, PDF, JPEG, PNG. File name restrictions: Use both English characters and numbers. Maximum document / image size: Not more than 300 x 300 DPI and pixel count should be less than 30 million',
        language: 'Evidence language: English',
        files: 'Supported files: TIFF, TIF, PDF, JPEG, JPG, PNG and maximum combined file sizes: 14.5MB across all files.'
      };
    }
    // Visa and other card brands (default)
    return {
      formats: 'Supported formats: TIFF, PDF, PNG, JPEG. Maximum individual file size: 2MB. Maximum page size for TIFF and JPEG: 8.64 inches x 14 inches',
      language: 'Evidence language: English',
      files: 'Supported files: TIFF, TIF, PDF, PNG, JPEG, JPG and maximum combined file sizes: 10MB across all files.'
    };
  };
  // Mock related orders data
  const relatedOrders: RelatedOrder[] = [{
    id: '1',
    orderId: '8b89fb9a-3fce-4aea-956f-49aa8d033caa',
    amount: 10000,
    currency: 'THB',
    createdTime: '2025-07-06 09:01:43 UTC+0800',
    status: 'Completed',
    matchingCriteria: 6,
    totalCriteria: 7
  }, {
    id: '2',
    orderId: 'bcd5898f-5af4-4bb8-99ee-6f2a308d799b',
    amount: 10000,
    currency: 'THB',
    createdTime: '2025-07-06 09:01:41 UTC+0800',
    status: 'Completed',
    matchingCriteria: 6,
    totalCriteria: 7
  }, {
    id: '3',
    orderId: '4086dc8d-fb0b-44d0-8e8c-cf018f0c778d',
    amount: 10000,
    currency: 'THB',
    createdTime: '2025-07-06 08:49:46 UTC+0800',
    status: 'Completed',
    matchingCriteria: 6,
    totalCriteria: 7
  }, {
    id: '4',
    orderId: 'a5cf0edf-9412-4ffe-9275-b8914ba25e25',
    amount: 10000,
    currency: 'THB',
    createdTime: '2025-07-06 08:49:33 UTC+0800',
    status: 'Completed',
    matchingCriteria: 6,
    totalCriteria: 7
  }, {
    id: '5',
    orderId: '6d92fede-b0c8-4efd-b633-230703873 56f',
    amount: 10000,
    currency: 'THB',
    createdTime: '2025-07-06 08:49:31 UTC+0800',
    status: 'Completed',
    matchingCriteria: 7,
    totalCriteria: 7
  }];
  const matchingCriteriaDetails: MatchingCriteriaDetail[] = [{
    category: 'Customer information',
    subtitle: 'Must match at least two criteria',
    items: [{
      label: 'IP address',
      matched: true
    }, {
      label: 'Device ID / fingerprint',
      matched: true
    }, {
      label: 'Shipping address',
      matched: true
    }, {
      label: 'Email address',
      matched: false
    }]
  }, {
    category: 'Transaction information',
    subtitle: 'Must match at least two criteria',
    items: [{
      label: '120 - 365 days older',
      matched: true
    }, {
      label: 'Undisputed & not fraudulent',
      matched: true
    }]
  }, {
    category: 'Additional information',
    subtitle: '',
    items: [{
      label: 'Payment method',
      matched: true
    }]
  }];
  const totalPages = Math.ceil(relatedOrders.length / rowsPerPage);
  const paginatedOrders = relatedOrders.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
  // Find the dispute by ID using centralized data
  const allDisputes = useMemo(() => generateDisputes(), []);
  const dispute = useMemo(() => {
    const found = allDisputes.find(d => d.id === id);
    if (!found) {
      // Return first dispute as fallback
      return allDisputes[0];
    }
    return found;
  }, [id, allDisputes]);
  // Check if dispute is challenged - either from the dispute status or from navigation state
  const isChallenged = dispute.status === 'challenged' || location.state?.challenged === true || hasBeenChallenged;
  // Get effective status (use accepted if user just accepted it)
  const effectiveStatus = isAccepted ? 'accepted' : dispute.status;
  const paymentAmount = dispute.amount;
  const disputedAmount = dispute.amount;
  const disputeFee = Math.round(dispute.amount * 0.08);
  const netAmount = disputedAmount + disputeFee;
  const formatCurrency = (amount: number, currency: string = 'THB') => {
    const value = (amount / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `THB ${value}`;
  };
  const getStatusBadge = (status: DisputeStatus) => {
    const styles = {
      requires_response: 'text-[#92400E] bg-[#FFEDD5]',
      expired: 'text-[#374151] bg-[#E5E7EB]',
      challenged: 'text-[#1D4ED8] bg-[#DBEAFE]',
      accepted: 'text-[#7C2D12] bg-[#FFEDD5]',
      reversed: 'text-[#166534] bg-[#DCFCE7]',
      pending_closure: 'text-[#4B5563] bg-[#E5E7EB]',
      pending_decision: 'text-[#1D4ED8] bg-[#DBEAFE]',
      won: 'text-[#166534] bg-[#DCFCE7]',
      lost: 'text-[#B91C1C] bg-[#FEE2E2]'
    };
    const labels = {
      requires_response: 'Requires response',
      expired: 'Expired',
      challenged: 'Challenged',
      accepted: 'Accepted',
      reversed: 'Reversed',
      pending_closure: 'Pending Closure',
      pending_decision: 'Pending Decision',
      won: 'Won',
      lost: 'Lost'
    };
    return <span className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium ${styles[status]}`} data-id="element-7691">
        {labels[status]}
      </span>;
  };
  const getHoursRemaining = (dueDate: string) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diffMs = due.getTime() - now.getTime();
    const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));
    return diffHours;
  };
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const getReasonDescription = (reason: DisputeReason): string => {
    const descriptions: Record<DisputeReason, string> = {
      duplicate: 'The customer claims this is a duplicate charge',
      fraudulent: 'The customer claims the transaction was fraudulent',
      subscription_canceled: 'The customer claims they canceled their subscription',
      product_unacceptable: 'The customer claims the product was unacceptable',
      product_not_received: 'The customer claims they did not receive the product',
      unrecognized: 'The customer does not recognize the transaction',
      credit_not_processed: 'The customer claims a credit was not processed',
      incorrect_account_details: 'The customer claims incorrect account details were used',
      insufficient_funds: 'The customer claims they had insufficient funds',
      bank_cannot_process: 'The bank cannot process the transaction',
      debit_not_authorized: 'The customer claims the debit was not authorized',
      general: 'General dispute reason',
      customer_initiated: 'Customer initiated the dispute',
      check_returned: 'The check was returned',
      noncompliant: 'The transaction was noncompliant'
    };
    return descriptions[reason] || 'Dispute reason not specified';
  };
  const getReasonLabel = (reason: DisputeReason): string => {
    const labels: Record<DisputeReason, string> = {
      duplicate: 'Duplicate',
      fraudulent: 'Fraudulent',
      subscription_canceled: 'Subscription canceled',
      product_unacceptable: 'Product unacceptable',
      product_not_received: 'Product not received',
      unrecognized: 'Unrecognized',
      credit_not_processed: 'Credit not processed',
      incorrect_account_details: 'Incorrect account details',
      insufficient_funds: 'Insufficient funds',
      bank_cannot_process: 'Bank cannot process',
      debit_not_authorized: 'Debit not authorized',
      general: 'General',
      customer_initiated: 'Customer initiated',
      check_returned: 'Check returned',
      noncompliant: 'Noncompliant'
    };
    return labels[reason];
  };
  const getPaymentSource = () => {
    // In a real app, this would come from the dispute data
    // For now, we'll use a simple logic based on dispute ID
    const sources = ['Payment Link', 'QR Code', 'Invoice', 'Tap to Phone', 'POS Terminal'];
    const sourceIcons = [LinkIcon, QrCodeIcon, FileIcon, SmartphoneIcon, TerminalIcon];
    const index = parseInt(dispute.id) % sources.length;
    return {
      name: sources[index],
      Icon: sourceIcons[index]
    };
  };
  const hoursRemaining = getHoursRemaining(dispute.dueDate);
  const reversedLifecycle = useMemo(() => {
    const lifecycle = [...dispute.lifecycle];
    if (isChallenged) {
      lifecycle.push({
        id: '3',
        timestamp: new Date().toISOString(),
        status: 'challenged' as DisputeStatus,
        description: 'Dispute challenged with supporting evidence'
      });
    }
    if (isAccepted) {
      lifecycle.push({
        id: String(lifecycle.length + 1),
        timestamp: new Date().toISOString(),
        status: 'accepted' as DisputeStatus,
        description: 'Dispute accepted by merchant'
      });
    }
    // Separate pinned and unpinned notes
    const pinnedNotes = merchantNotes.filter(note => note.isPinned);
    const unpinnedNotes = merchantNotes.filter(note => !note.isPinned);
    // Sort unpinned notes by timestamp
    const sortedUnpinnedNotes = unpinnedNotes.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    // Combine: pinned notes first (sorted by timestamp), then lifecycle events and unpinned notes
    const allEvents = [...lifecycle, ...sortedUnpinnedNotes];
    const sortedEvents = allEvents.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    // Add pinned notes at the top
    return [...pinnedNotes.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()), ...sortedEvents];
  }, [dispute.lifecycle, isChallenged, isAccepted, merchantNotes]);
  // Get evidence recommendations using the centralized utility
  const evidenceRecs = useMemo(() => {
    // LOGGING: Start of evidence recommendation lookup
    console.group('🔍 Evidence Recommendation Lookup');
    console.log('Dispute ID:', dispute.id);
    console.log('Card Brand (raw):', dispute.cardBrand);
    console.log('Network Reason Code (raw):', dispute.networkReasonCode);
    console.log('Dispute Reason:', dispute.reason);
    const result = getEvidenceRecommendation(dispute.cardBrand, dispute.networkReasonCode);
    console.log('Lookup Result:', {
      title: result.title,
      description: result.description,
      recommendationsCount: result.recommendations.length,
      recommendations: result.recommendations.map(r => r.title)
    });
    console.groupEnd();
    return result;
  }, [dispute.cardBrand, dispute.networkReasonCode]);
  // If challenge flow is active, show full-screen challenge interface
  if (showChallengeFlow) {
    return <div className="min-h-screen bg-white" data-id="element-7692">
        {/* Header */}
        <div className="border-b border-gray-200" data-id="element-7693">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-7694">
            <div className="flex items-center gap-3" data-id="element-7695">
              <div className="flex items-center gap-2" data-id="element-7696">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7697">
                  <path d="M3 7L12 2L21 7L12 12L3 7Z" fill="#FF6B35" stroke="#FF6B35" strokeWidth="2" strokeLinejoin="round" data-id="element-7698" />
                  <path d="M3 12L12 17L21 12" stroke="#FF6B35" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-7699" />
                  <path d="M3 17L12 22L21 17" stroke="#FF6B35" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-7700" />
                </svg>
                <span className="text-lg font-semibold" data-id="element-7701">Airwallex</span>
              </div>
              <span className="text-gray-400" data-id="element-7702">|</span>
              <span className="text-gray-700" data-id="element-7703">Challenge dispute</span>
            </div>
            <button onClick={handleCloseChallengeFlow} className="text-gray-400 hover:text-gray-600" data-id="element-7704">
              <CloseIcon size={24} data-id="element-7705" />
            </button>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-8" data-id="element-7706">
          <div className="flex gap-8" data-id="element-7707">
            {/* Left Sidebar */}
            <div className="w-64 flex-shrink-0" data-id="element-7708">
              <div className="space-y-1" data-id="element-7709">
                <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between ${expandedSections.challengeDetails ? 'bg-gray-100' : ''}`} onClick={() => toggleSection('challengeDetails')} data-id="element-7710">
                  <span className="font-medium" data-id="element-7711">Challenge details</span>
                  {expandedSections.challengeDetails ? <ChevronUpIcon size={16} data-id="element-7712" /> : <ChevronDownIcon size={16} data-id="element-7713" />}
                </button>
                {expandedSections.challengeDetails && <div className="ml-3 space-y-1" data-id="element-7714">
                    <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'reason' ? 'text-blue-600 bg-blue-50' : 'text-gray-700'}`} data-id="element-7715">
                      {['evidence', 'order', 'customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-7716" />}
                      Challenge reason
                    </button>
                    <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'evidence' ? 'text-blue-600 bg-blue-50' : challengeStep === 'reason' ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-7717">
                      {['order', 'customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-7718" />}
                      Supporting evidence
                    </button>
                  </div>}

                <button className="w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between text-gray-500" onClick={() => toggleSection('transactionDetails')} data-id="element-7719">
                  <span data-id="element-7720">Transaction details</span>
                  {expandedSections.transactionDetails ? <ChevronUpIcon size={16} data-id="element-7721" /> : <ChevronDownIcon size={16} data-id="element-7722" />}
                </button>
                {expandedSections.transactionDetails && <div className="ml-3 space-y-1" data-id="element-7723">
                    <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'order' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-7724">
                      {['customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-7725" />}
                      Order details
                    </button>
                    <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'customer' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence', 'order'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-7726">
                      {['other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-7727" />}
                      Customer details
                    </button>
                  </div>}

                <button className="w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between text-gray-500" onClick={() => toggleSection('otherEvidence')} data-id="element-7728">
                  <span data-id="element-7729">Other evidence</span>
                  {expandedSections.otherEvidence ? <ChevronUpIcon size={16} data-id="element-7730" /> : <ChevronDownIcon size={16} data-id="element-7731" />}
                </button>
                {expandedSections.otherEvidence && <div className="ml-3 space-y-1" data-id="element-7732">
                    <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'other' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence', 'order', 'customer'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-7733">
                      {challengeStep === 'review' && <CheckCircleIcon size={16} className="text-green-600" data-id="element-7734" />}
                      Other supporting documents
                    </button>
                  </div>}
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1" data-id="element-7735">
              {challengeStep === 'reason' && <div className="max-w-3xl" data-id="element-7736">
                  <div className="flex items-start justify-between mb-6" data-id="element-7737">
                    <div data-id="element-7738">
                      <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-7739">
                        Challenge reason
                      </h1>
                      <p className="text-gray-600" data-id="element-7740">
                        Your responses will help us recommend the best evidence
                        to submit for your challenge.
                      </p>
                    </div>
                    <div className="text-right" data-id="element-7741">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-7742">
                        Dispute ID: {dispute.disputeId}
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-7743">
                        View details
                      </button>
                      <div className="mt-2 text-sm" data-id="element-7744">
                        <div className="text-gray-500" data-id="element-7745">Dispute amount</div>
                        <div className="font-medium" data-id="element-7746">
                          {formatCurrency(dispute.amount)}
                        </div>
                      </div>
                      <div className="mt-2 text-sm" data-id="element-7747">
                        <div className="text-gray-500" data-id="element-7748">Response due</div>
                        <div className="font-medium" data-id="element-7749">
                          {formatDateTime(dispute.dueDate)}
                        </div>
                        <div className="text-red-600 font-medium" data-id="element-7750">
                          Due in 1 hour
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6" data-id="element-7751">
                    <div data-id="element-7752">
                      <label className="block text-sm font-medium text-gray-900 mb-3" data-id="element-7753">
                        Why are you challenging the dispute?
                      </label>
                      <p className="text-sm text-gray-600 mb-3" data-id="element-7754">
                        Choose the closest option.
                      </p>
                      <div className="space-y-3" data-id="element-7755">
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-7756">
                          <input type="radio" name="challengeReason" value="withdrew" checked={selectedChallengeReason === 'withdrew'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-1" data-id="element-7757" />
                          <span className="text-sm text-gray-900" data-id="element-7758">
                            The customer withdrew the dispute
                          </span>
                        </label>
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-7759">
                          <input type="radio" name="challengeReason" value="refunded" checked={selectedChallengeReason === 'refunded'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-1" data-id="element-7760" />
                          <span className="text-sm text-gray-900" data-id="element-7761">
                            The customer has already been refunded
                          </span>
                        </label>
                        <label className="flex items-start gap-3 p-3 border-2 border-blue-600 bg-blue-50 rounded-lg cursor-pointer" data-id="element-7762">
                          <input type="radio" name="challengeReason" value="purchasing_history" checked={selectedChallengeReason === 'purchasing_history'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-1" data-id="element-7763" />
                          <div className="flex-1" data-id="element-7764">
                            <div className="text-sm text-gray-900 font-medium" data-id="element-7765">
                              The customer has a purchasing history with me
                            </div>
                            {selectedChallengeReason === 'purchasing_history' && <div className="mt-3 p-3 bg-white rounded border border-blue-200" data-id="element-7766">
                                <p className="text-sm text-gray-700" data-id="element-7767">
                                  We recommend this reason as we have identified
                                  transactions that prove the customer has a
                                  purchasing history with you.
                                </p>
                              </div>}
                          </div>
                        </label>
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-7768">
                          <input type="radio" name="challengeReason" value="other" checked={selectedChallengeReason === 'other'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-1" data-id="element-7769" />
                          <span className="text-sm text-gray-900" data-id="element-7770">
                            Other reasons
                          </span>
                        </label>
                      </div>
                    </div>

                    <div data-id="element-7771">
                      <label className="block text-sm font-medium text-gray-900 mb-3" data-id="element-7772">
                        What type of product/service was the disputed payment
                        for?
                      </label>
                      <p className="text-sm text-gray-600 mb-3" data-id="element-7773">
                        Choose the closest option.
                      </p>
                      <div className="space-y-3" data-id="element-7774">
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-7775">
                          <input type="radio" name="productType" value="physical" checked={selectedProductType === 'physical'} onChange={e => setSelectedProductType(e.target.value)} className="mt-1" data-id="element-7776" />
                          <div data-id="element-7777">
                            <div className="text-sm text-gray-900 font-medium" data-id="element-7778">
                              Physical product
                            </div>
                            <div className="text-xs text-gray-500 mt-1" data-id="element-7779">
                              Physical goods to be delivered to the customer,
                              e.g. clothes, accessories, electronic gadgets
                            </div>
                          </div>
                        </label>
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-7780">
                          <input type="radio" name="productType" value="digital" checked={selectedProductType === 'digital'} onChange={e => setSelectedProductType(e.target.value)} className="mt-1" data-id="element-7781" />
                          <div data-id="element-7782">
                            <div className="text-sm text-gray-900 font-medium" data-id="element-7783">
                              Digital product or service
                            </div>
                            <div className="text-xs text-gray-500 mt-1" data-id="element-7784">
                              Purchased access to software, digital files or
                              experiences, e.g. mobile apps, music service,
                              digital tickets
                            </div>
                          </div>
                        </label>
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-7785">
                          <input type="radio" name="productType" value="offline" checked={selectedProductType === 'offline'} onChange={e => setSelectedProductType(e.target.value)} className="mt-1" data-id="element-7786" />
                          <div data-id="element-7787">
                            <div className="text-sm text-gray-900 font-medium" data-id="element-7788">
                              Offline service
                            </div>
                            <div className="text-xs text-gray-500 mt-1" data-id="element-7789">
                              Labour or assistant services provided either at a
                              specific location or remotely, e.g.
                              transportation, home servicing
                            </div>
                          </div>
                        </label>
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-7790">
                          <input type="radio" name="productType" value="reservation" checked={selectedProductType === 'reservation'} onChange={e => setSelectedProductType(e.target.value)} className="mt-1" data-id="element-7791" />
                          <div data-id="element-7792">
                            <div className="text-sm text-gray-900 font-medium" data-id="element-7793">
                              Reservation or booking
                            </div>
                            <div className="text-xs text-gray-500 mt-1" data-id="element-7794">
                              Services related to reservations or bookings, e.g.
                              flight tickets, hotel booking
                            </div>
                          </div>
                        </label>
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-7795">
                          <input type="radio" name="productType" value="other_product" checked={selectedProductType === 'other_product'} onChange={e => setSelectedProductType(e.target.value)} className="mt-1" data-id="element-7796" />
                          <span className="text-sm text-gray-900" data-id="element-7797">Other</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-7798">
                    <button onClick={handleCloseChallengeFlow} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-7799">
                      Cancel
                    </button>
                    <button onClick={handleChallengeReasonNext} disabled={!selectedProductType} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-7800">
                      Next
                    </button>
                  </div>
                </div>}

              {challengeStep === 'evidence' && <div className="max-w-3xl" data-id="element-7801">
                  <div className="flex items-start justify-between mb-6" data-id="element-7802">
                    <div data-id="element-7803">
                      <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-7804">
                        {evidenceRecs.title}
                      </h1>
                      <p className="text-gray-600" data-id="element-7805">
                        {evidenceRecs.description}
                        {evidenceRecs.learnMoreLink && <>
                            {' '}
                            <a href="#" className="text-blue-600 hover:text-blue-700" data-id="element-7806">
                              {evidenceRecs.learnMoreLink}
                            </a>
                          </>}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-7807">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-7808">
                        Dispute ID: {dispute.disputeId}
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-7809">
                        View details
                      </button>
                      <div className="mt-2 text-sm" data-id="element-7810">
                        <div className="text-gray-500" data-id="element-7811">Dispute amount</div>
                        <div className="font-medium" data-id="element-7812">
                          {formatCurrency(dispute.amount)}
                        </div>
                      </div>
                      <div className="mt-2 text-sm" data-id="element-7813">
                        <div className="text-gray-500" data-id="element-7814">Response due</div>
                        <div className="font-medium" data-id="element-7815">
                          {formatDateTime(dispute.dueDate)}
                        </div>
                        <div className="text-red-600 font-medium" data-id="element-7816">
                          Due in 1 hour
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-6" data-id="element-7817">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-7818">
                      Provide supporting evidence
                    </h3>
                    {/* LOGGING: Verify evidence is being rendered */}
                    {console.log('🎨 Rendering evidence step with:', {
                  title: evidenceRecs.title,
                  recommendationsCount: evidenceRecs.recommendations.length,
                  firstRecommendation: evidenceRecs.recommendations[0]
                })}
                    {dispute.networkReasonCode === '13.2' && <p className="text-sm text-gray-600 mb-4" data-id="element-7819">
                        For Visa 13.2 (Subscription Canceled) disputes, we
                        recommend you provide the following evidence:
                      </p>}
                    {dispute.reason === 'subscription_canceled' && dispute.networkReasonCode !== '13.2' && <p className="text-sm text-gray-600 mb-4" data-id="element-7820">
                          For subscription cancellation disputes, we recommend
                          you provide the following evidence:
                        </p>}
                    {dispute.reason !== 'subscription_canceled' && <p className="text-sm text-gray-600 mb-4" data-id="element-7821">
                        Based on your challenge reason, we recommend providing:
                      </p>}
                    <ul className="list-disc pl-5 space-y-2 text-sm text-gray-700 mb-4" data-id="element-7822">
                      {evidenceRecs.recommendations.map((rec, index) => <li key={index} data-id="element-7823">
                          <strong data-id="element-7824">{rec.title}</strong>
                          {rec.description && `: ${rec.description}`}
                        </li>)}
                    </ul>
                    <p className="text-sm text-gray-500" data-id="element-7825">
                      {(() => {
                    const requirements = getFileFormatRequirements();
                    return <>
                            {requirements.formats}
                            <br data-id="element-7826" />
                            {requirements.language}
                            <br data-id="element-7827" />
                            {requirements.files}
                          </>;
                  })()}
                    </p>
                  </div>

                  <div className="mb-6" data-id="element-7828">
                    <button disabled={noEvidenceChecked} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-7829">
                      <UploadIcon size={16} data-id="element-7830" />
                      Upload file
                    </button>
                  </div>

                  <div className="mb-6" data-id="element-7831">
                    <textarea placeholder="Include any additional file descriptions or explanations here. (Optional)" rows={4} disabled={noEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-7832" />
                  </div>

                  <div className="mb-6" data-id="element-7833">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-7834">
                      <input type="checkbox" checked={noEvidenceChecked} onChange={e => setNoEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-7835" />
                      <span className="text-sm text-gray-900" data-id="element-7836">
                        I do not have any evidence to provide for this step
                      </span>
                    </label>
                  </div>

                  <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-7837">
                    <button onClick={() => setChallengeStep('reason')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-7838">
                      Back
                    </button>
                    <button onClick={handleEvidenceNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-7839">
                      Next
                    </button>
                  </div>
                </div>}

              {challengeStep === 'order' && <div className="max-w-3xl" data-id="element-7840">
                  <div className="flex items-start justify-between mb-6" data-id="element-7841">
                    <div data-id="element-7842">
                      <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-7843">
                        Order details
                      </h1>
                      <p className="text-gray-600" data-id="element-7844">
                        Please provide as much information as possible about the
                        goods/service the payment was disputed for. This will
                        allow the issuer to verify that the evidence you
                        provided corresponds to the transaction disputed by the
                        customer.
                      </p>
                      <p className="text-gray-600 mt-2" data-id="element-7845">
                        The details below have been pre-filled from transaction
                        data. Please review and edit the information if needed.
                      </p>
                    </div>
                    <div className="text-right" data-id="element-7846">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-7847">
                        Dispute ID: {dispute.disputeId}
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-7848">
                        View details
                      </button>
                      <div className="mt-2 text-sm" data-id="element-7849">
                        <div className="text-gray-500" data-id="element-7850">Dispute amount</div>
                        <div className="font-medium" data-id="element-7851">
                          {formatCurrency(dispute.amount)}
                        </div>
                      </div>
                      <div className="mt-2 text-sm" data-id="element-7852">
                        <div className="text-gray-500" data-id="element-7853">Response due</div>
                        <div className="font-medium" data-id="element-7854">
                          {formatDateTime(dispute.dueDate)}
                        </div>
                        <div className="text-red-600 font-medium" data-id="element-7855">
                          Due in 1 hour
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6" data-id="element-7856">
                    <div data-id="element-7857">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-7858">
                        Product details
                        <button className="ml-2 text-blue-600 hover:text-blue-700" data-id="element-7859">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7860">
                            <path d="M8 12V8M8 4H8.01M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-7861" />
                          </svg>
                        </button>
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-7862">
                        <span data-id="element-7863">IPHONE7 * 5, 100.01 THB</span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-7864">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7865">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-7866" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-7867">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-7868">
                        Shipping address
                        <button className="ml-2 text-blue-600 hover:text-blue-700" data-id="element-7869">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7870">
                            <path d="M8 12V8M8 4H8.01M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-7871" />
                          </svg>
                        </button>
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-7872">
                        <span data-id="element-7873">
                          Rabbit Gateway Office, 123 Silom Road, Bang Rak,
                          Bangkok 10500, Thailand
                        </span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-7874">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7875">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-7876" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-7877">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-7878">
                        Shipping carrier
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-7879">
                        <span data-id="element-7880">sameday</span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-7881">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7882">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-7883" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-7884">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-7885">
                        Provide additional supporting evidence
                      </h3>
                      <p className="text-sm text-gray-600 mb-4" data-id="element-7886">
                        As the payment was for a{' '}
                        <span className="font-medium" data-id="element-7887">Physical product</span>,
                        we recommend you to also provide the following
                        information:
                      </p>
                      <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700 mb-4" data-id="element-7888">
                        <li data-id="element-7889">Shipping date</li>
                        <li data-id="element-7890">Shipping tracking number</li>
                      </ul>
                      <p className="text-sm text-gray-600 mb-4" data-id="element-7891">
                        You can upload any document containing such information
                        (e.g. invoices, shipping documentation, delivery
                        confirmation or shipping receipts) below.
                      </p>
                      <p className="text-sm text-gray-500 mb-4" data-id="element-7892">
                        {(() => {
                      const requirements = getFileFormatRequirements();
                      return <>
                              {requirements.formats}
                              <br data-id="element-7893" />
                              {requirements.language}
                              <br data-id="element-7894" />
                              {requirements.files}
                            </>;
                    })()}
                      </p>
                    </div>

                    <div data-id="element-7895">
                      <button disabled={noOrderEvidenceChecked} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noOrderEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-7896">
                        <UploadIcon size={16} data-id="element-7897" />
                        Upload file
                      </button>
                    </div>

                    <div data-id="element-7898">
                      <textarea placeholder="Include any additional file descriptions or explanations here. (Optional)" rows={4} disabled={noOrderEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noOrderEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-7899" />
                    </div>

                    <div data-id="element-7900">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-7901">
                        <input type="checkbox" checked={noOrderEvidenceChecked} onChange={e => setNoOrderEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-7902" />
                        <span className="text-sm text-gray-900" data-id="element-7903">
                          I do not have any evidence to provide for this step
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-7904">
                    <button onClick={() => setChallengeStep('evidence')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-7905">
                      Back
                    </button>
                    <button onClick={handleOrderNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-7906">
                      Next
                    </button>
                  </div>
                </div>}

              {challengeStep === 'customer' && <div className="max-w-3xl" data-id="element-7907">
                  <div className="flex items-start justify-between mb-6" data-id="element-7908">
                    <div data-id="element-7909">
                      <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-7910">
                        Customer details
                      </h1>
                      <p className="text-gray-600" data-id="element-7911">
                        Please provide as much information as possible about the
                        customer. This will allow the issuer to verify that the
                        evidence you provided corresponds to the customer
                        disputing the transaction.
                      </p>
                      <p className="text-gray-600 mt-2" data-id="element-7912">
                        The details below have been pre-filled from transaction
                        data. Please review and edit the information if needed.
                      </p>
                    </div>
                    <div className="text-right" data-id="element-7913">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-7914">
                        Dispute ID: {dispute.disputeId}
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-7915">
                        View details
                      </button>
                      <div className="mt-2 text-sm" data-id="element-7916">
                        <div className="text-gray-500" data-id="element-7917">Dispute amount</div>
                        <div className="font-medium" data-id="element-7918">
                          {formatCurrency(dispute.amount)}
                        </div>
                      </div>
                      <div className="mt-2 text-sm" data-id="element-7919">
                        <div className="text-gray-500" data-id="element-7920">Response due</div>
                        <div className="font-medium" data-id="element-7921">
                          {formatDateTime(dispute.dueDate)}
                        </div>
                        <div className="text-red-600 font-medium" data-id="element-7922">
                          Due in 1 hour
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6" data-id="element-7923">
                    <div data-id="element-7924">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-7925">
                        Customer name
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-7926">
                        <span data-id="element-7927">Test name</span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-7928">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7929">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-7930" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-7931">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-7932">
                        Customer email
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-7933">
                        <span data-id="element-7934">jessica@airwallex.com</span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-7935">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7936">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-7937" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-7938">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-7939">
                        Customer purchase IP
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-7940">
                        <span data-id="element-7941">35.221.170.121</span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-7942">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7943">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-7944" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-7945">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-7946">
                        Customer phone
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-7947">
                        <span data-id="element-7948">+60 12 345 6789</span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-7949">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7950">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-7951" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-7952">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-7953">
                        Customer billing address
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-7954">
                        <span data-id="element-7955">
                          Rabbit Gateway Office, 123 Silom Road, Bang Rak,
                          Bangkok 10500, Thailand
                        </span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-7956">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7957">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-7958" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-7959">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-7960">
                        Provide supporting evidence
                      </h3>
                      <p className="text-sm text-gray-600 mb-4" data-id="element-7961">
                        We recommend you to provide the following information
                        about the customer:
                      </p>
                      <p className="text-sm text-gray-600 mb-4" data-id="element-7962">
                        You can upload any document containing such information
                        (e.g. invoices, receipts) below.
                      </p>
                      <p className="text-sm text-gray-500 mb-4" data-id="element-7963">
                        {(() => {
                      const requirements = getFileFormatRequirements();
                      return <>
                              {requirements.formats}
                              <br data-id="element-7964" />
                              {requirements.language}
                              <br data-id="element-7965" />
                              {requirements.files}
                            </>;
                    })()}
                      </p>
                    </div>

                    <div data-id="element-7966">
                      <button disabled={noCustomerEvidenceChecked} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noCustomerEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-7967">
                        <UploadIcon size={16} data-id="element-7968" />
                        Upload file
                      </button>
                    </div>

                    <div data-id="element-7969">
                      <textarea placeholder="Include any additional file descriptions or explanations here. (Optional)" rows={4} disabled={noCustomerEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noCustomerEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-7970" />
                    </div>

                    <div data-id="element-7971">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-7972">
                        <input type="checkbox" checked={noCustomerEvidenceChecked} onChange={e => setNoCustomerEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-7973" />
                        <span className="text-sm text-gray-900" data-id="element-7974">
                          I do not have any evidence to provide for this step
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-7975">
                    <button onClick={() => setChallengeStep('order')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-7976">
                      Back
                    </button>
                    <button onClick={handleCustomerNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-7977">
                      Next
                    </button>
                  </div>
                </div>}

              {challengeStep === 'other' && <div className="max-w-3xl" data-id="element-7978">
                  <div className="flex items-start justify-between mb-6" data-id="element-7979">
                    <div data-id="element-7980">
                      <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-7981">
                        Other supporting documents
                      </h1>
                      <p className="text-gray-600" data-id="element-7982">
                        If you have any other document(s) that you believe is
                        valuable to assess the legitimacy of your challenge, you
                        can add it below.
                      </p>
                    </div>
                    <div className="text-right" data-id="element-7983">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-7984">
                        Dispute ID: {dispute.disputeId}
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-7985">
                        View details
                      </button>
                      <div className="mt-2 text-sm" data-id="element-7986">
                        <div className="text-gray-500" data-id="element-7987">Dispute amount</div>
                        <div className="font-medium" data-id="element-7988">
                          {formatCurrency(dispute.amount)}
                        </div>
                      </div>
                      <div className="mt-2 text-sm" data-id="element-7989">
                        <div className="text-gray-500" data-id="element-7990">Response due</div>
                        <div className="font-medium" data-id="element-7991">
                          {formatDateTime(dispute.dueDate)}
                        </div>
                        <div className="text-red-600 font-medium" data-id="element-7992">
                          Due in 1 hour
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6" data-id="element-7993">
                    <div data-id="element-7994">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-7995">
                        Provide supporting evidence
                      </h3>
                      <p className="text-sm text-gray-500 mb-4" data-id="element-7996">
                        {(() => {
                      const requirements = getFileFormatRequirements();
                      return <>
                              {requirements.formats}
                              <br data-id="element-7997" />
                              {requirements.language}
                              <br data-id="element-7998" />
                              {requirements.files}
                            </>;
                    })()}
                      </p>
                    </div>

                    <div data-id="element-7999">
                      <button disabled={noOtherEvidenceChecked} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noOtherEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-8000">
                        <UploadIcon size={16} data-id="element-8001" />
                        Upload file
                      </button>
                    </div>

                    <div data-id="element-8002">
                      <textarea placeholder="Include any additional file descriptions or explanations here. (Optional)" rows={4} disabled={noOtherEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noOtherEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-8003" />
                    </div>

                    <div data-id="element-8004">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-8005">
                        <input type="checkbox" checked={noOtherEvidenceChecked} onChange={e => setNoOtherEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-8006" />
                        <span className="text-sm text-gray-900" data-id="element-8007">
                          I do not have any evidence to provide for this step
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-8008">
                    <button onClick={() => setChallengeStep('customer')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-8009">
                      Back
                    </button>
                    <button onClick={handleOtherNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-8010">
                      Review
                    </button>
                  </div>
                </div>}

              {challengeStep === 'review' && <div className="max-w-3xl" data-id="element-8011">
                  <div className="flex items-start justify-between mb-6" data-id="element-8012">
                    <div data-id="element-8013">
                      <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-8014">
                        Review your challenge
                      </h1>
                      <p className="text-gray-600" data-id="element-8015">
                        Please confirm that the information you have provided is
                        correct before submission.
                      </p>
                    </div>
                    <div className="text-right" data-id="element-8016">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-8017">
                        Dispute ID: {dispute.disputeId}
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-8018">
                        View details
                      </button>
                      <div className="mt-2 text-sm" data-id="element-8019">
                        <div className="text-gray-500" data-id="element-8020">Dispute amount</div>
                        <div className="font-medium" data-id="element-8021">
                          {formatCurrency(dispute.amount)}
                        </div>
                      </div>
                      <div className="mt-2 text-sm" data-id="element-8022">
                        <div className="text-gray-500" data-id="element-8023">Response due</div>
                        <div className="font-medium" data-id="element-8024">
                          {formatDateTime(dispute.dueDate)}
                        </div>
                        <div className="text-red-600 font-medium" data-id="element-8025">
                          Due in 1 hour
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6" data-id="element-8026">
                    {/* Challenge details section */}
                    <div className="bg-gray-50 rounded-lg p-6" data-id="element-8027">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-8028">
                        Challenge details
                      </h3>
                      <div className="space-y-3" data-id="element-8029">
                        <div className="flex justify-between" data-id="element-8030">
                          <span className="text-sm text-gray-600" data-id="element-8031">
                            Reason for challenge
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-8032">
                            The customer has a purchasing history with me
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-8033">
                          <span className="text-sm text-gray-600" data-id="element-8034">
                            Type of product or service
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-8035">
                            Physical product
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Transaction details section */}
                    <div className="bg-gray-50 rounded-lg p-6" data-id="element-8036">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-8037">
                        Transaction details
                      </h3>
                      <div className="space-y-3" data-id="element-8038">
                        <div className="flex justify-between" data-id="element-8039">
                          <span className="text-sm text-gray-600" data-id="element-8040">
                            Product details
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-8041">
                            IPHONE7 * 5, 100.01 THB
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-8042">
                          <span className="text-sm text-gray-600" data-id="element-8043">
                            Shipping address
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-8044">
                            Rabbit Gateway Office, 123 Silom Road, Bang Rak,
                            Bangkok 10500, Thailand
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-8045">
                          <span className="text-sm text-gray-600" data-id="element-8046">
                            Customer name
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-8047">
                            Test name
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-8048">
                          <span className="text-sm text-gray-600" data-id="element-8049">
                            Customer email
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-8050">
                            jessica@airwallex.com
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-8051">
                          <span className="text-sm text-gray-600" data-id="element-8052">
                            Customer purchase IP
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-8053">
                            35.221.170.121
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-8054">
                          <span className="text-sm text-gray-600" data-id="element-8055">
                            Customer phone
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-8056">
                            +60 12 345 6789
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-8057">
                          <span className="text-sm text-gray-600" data-id="element-8058">
                            Customer billing address
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-8059">
                            Rabbit Gateway Office, 123 Silom Road, Bang Rak,
                            Bangkok 10500, Thailand
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Warning if no evidence */}
                    {(noEvidenceChecked || noOrderEvidenceChecked || noCustomerEvidenceChecked || noOtherEvidenceChecked) && <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-id="element-8060">
                        <AlertTriangleIcon size={20} className="text-red-600 flex-shrink-0 mt-0.5" data-id="element-8061" />
                        <p className="text-sm text-gray-900" data-id="element-8062">
                          Please attach at least one of the recommended evidence
                          types to proceed with the response.
                        </p>
                      </div>}

                    {/* Confirmation checkbox */}
                    <div data-id="element-8063">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-8064">
                        <input type="checkbox" checked={reviewConfirmChecked} onChange={e => setReviewConfirmChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-8065" />
                        <span className="text-sm text-gray-900" data-id="element-8066">
                          I understand that the current evidence can only be
                          submitted once for the issuer's review, and cannot be
                          reused.
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-8067">
                    <button onClick={handleReviewBack} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-8068">
                      Back
                    </button>
                    <button disabled={!reviewConfirmChecked} className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-8069">
                      Submit
                    </button>
                  </div>
                </div>}
            </div>
          </div>
        </div>

        {/* No Evidence Warning Modal */}
        {showNoEvidenceWarning && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-8070">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-8071">
              <div className="fixed inset-0 transition-opacity" onClick={() => setShowNoEvidenceWarning(false)} data-id="element-8072">
                <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-8073"></div>
              </div>

              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-8074">
                &#8203;
              </span>

              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-8075">
                <div className="bg-white px-6 pt-6 pb-4" data-id="element-8076">
                  <div className="flex items-start gap-4" data-id="element-8077">
                    <div className="flex-shrink-0" data-id="element-8078">
                      <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-8079">
                        <AlertTriangleIcon size={24} className="text-red-600" data-id="element-8080" />
                      </div>
                    </div>
                    <div className="flex-1" data-id="element-8081">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-8082">
                        Are you sure you want to proceed without evidence?
                      </h3>
                      <p className="text-sm text-gray-600" data-id="element-8083">
                        Submitting a challenge without supporting evidence
                        significantly reduces your chances of winning the
                        dispute. We strongly recommend providing at least one
                        type of evidence to support your case.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3" data-id="element-8084">
                  <button onClick={() => setShowNoEvidenceWarning(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900" data-id="element-8085">
                    Go back and add evidence
                  </button>
                  <button onClick={() => {
                setShowNoEvidenceWarning(false);
                setChallengeStep('review');
              }} className="px-6 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700" data-id="element-8086">
                    Proceed anyway
                  </button>
                </div>
              </div>
            </div>
          </div>}
      </div>;
  }
  return <div className="space-y-6" data-id="element-8087">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-8088">
        <button onClick={() => navigate('/disputes')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-8089">
          <ArrowLeftIcon size={20} data-id="element-8090" />
          Back to Disputes
        </button>
      </div>

      {/* Dispute Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-8091">
        <div className="flex items-start justify-between mb-6" data-id="element-8092">
          <div className="flex items-center gap-3" data-id="element-8093">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-8094">Dispute</h1>
            {getStatusBadge(effectiveStatus)}
          </div>
          <button onClick={() => setShowHelpModal(true)} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700" data-id="element-8095">
            {isChallenged ? 'What happens after a dispute is challenged?' : 'How to respond to a dispute?'}
            <HelpCircleIcon size={16} data-id="element-8096" />
          </button>
        </div>

        <div className="mb-6" data-id="element-8097">
          <div className="text-sm text-gray-500" data-id="element-8098">
            Dispute ID: {dispute.disputeId}
          </div>
        </div>

        {isChallenged && <div className="mb-6 p-5 bg-blue-50 border-2 border-blue-200 rounded-lg" data-id="element-8099">
            <div className="flex items-start gap-4" data-id="element-8100">
              <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0" data-id="element-8101">
                <CheckCircleIcon size={20} className="text-white" data-id="element-8102" />
              </div>
              <div className="flex-1" data-id="element-8103">
                <div className="text-base font-semibold text-gray-900 mb-2" data-id="element-8104">
                  Challenge submitted
                </div>
                <div className="text-sm text-gray-700 mb-4" data-id="element-8105">
                  Your challenge has been submitted and is being reviewed by the
                  issuing bank. You will be notified of any updates.
                </div>
                <button onClick={handleViewChallengeDetails} className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-sm transition-colors" data-id="element-8106">
                  <FileTextIcon size={18} data-id="element-8107" />
                  View challenge details
                </button>
              </div>
            </div>
          </div>}

        {dispute.status === 'won' && <div className="mb-6 p-5 bg-green-50 border-2 border-green-200 rounded-lg" data-id="element-8108">
            <div className="flex items-start gap-4" data-id="element-8109">
              <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0" data-id="element-8110">
                <CheckCircleIcon size={20} className="text-white" data-id="element-8111" />
              </div>
              <div className="flex-1" data-id="element-8112">
                <div className="text-base font-semibold text-gray-900 mb-2" data-id="element-8113">
                  Dispute won
                </div>
                <div className="text-sm text-gray-700 mb-4" data-id="element-8114">
                  The issuing bank has reviewed your evidence and ruled in your
                  favor. The disputed amount has been credited back to your
                  account.
                </div>
                <button onClick={handleViewChallengeDetails} className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 shadow-sm transition-colors" data-id="element-8115">
                  <FileTextIcon size={18} data-id="element-8116" />
                  View your submitted evidence
                </button>
              </div>
            </div>
          </div>}

        {dispute.status === 'reversed' && <div className="mb-6 p-5 bg-green-50 border-2 border-green-200 rounded-lg" data-id="element-8117">
            <div className="flex items-start gap-4" data-id="element-8118">
              <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0" data-id="element-8119">
                <CheckCircleIcon size={20} className="text-white" data-id="element-8120" />
              </div>
              <div className="flex-1" data-id="element-8121">
                <div className="text-base font-semibold text-gray-900 mb-2" data-id="element-8122">
                  Dispute reversed
                </div>
                <div className="text-sm text-gray-700" data-id="element-8123">
                  The cardholder has withdrawn their dispute. The disputed
                  amount has been credited back to your account.
                </div>
              </div>
            </div>
          </div>}

        {dispute.status === 'accepted' && <div className="mb-6 p-5 bg-orange-50 border-2 border-orange-200 rounded-lg" data-id="element-8124">
            <div className="flex items-start gap-4" data-id="element-8125">
              <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0" data-id="element-8126">
                <CheckCircleIcon size={20} className="text-white" data-id="element-8127" />
              </div>
              <div className="flex-1" data-id="element-8128">
                <div className="text-base font-semibold text-gray-900 mb-2" data-id="element-8129">
                  Dispute accepted
                </div>
                <div className="text-sm text-gray-700" data-id="element-8130">
                  You have accepted this dispute. The disputed amount will not
                  be credited back to your account.
                </div>
              </div>
            </div>
          </div>}

        {isAccepted && <div className="mb-6 p-5 bg-orange-50 border-2 border-orange-200 rounded-lg" data-id="element-8131">
            <div className="flex items-start gap-4" data-id="element-8132">
              <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0" data-id="element-8133">
                <CheckCircleIcon size={20} className="text-white" data-id="element-8134" />
              </div>
              <div className="flex-1" data-id="element-8135">
                <div className="text-base font-semibold text-gray-900 mb-2" data-id="element-8136">
                  Dispute accepted
                </div>
                <div className="text-sm text-gray-700" data-id="element-8137">
                  You have accepted this dispute. The disputed amount will not
                  be credited back to your account.
                </div>
              </div>
            </div>
          </div>}

        {dispute.status === 'lost' && <div className="mb-6 p-5 bg-red-50 border-2 border-red-200 rounded-lg" data-id="element-8138">
            <div className="flex items-start gap-4" data-id="element-8139">
              <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0" data-id="element-8140">
                <XCircleIcon size={20} className="text-white" data-id="element-8141" />
              </div>
              <div className="flex-1" data-id="element-8142">
                <div className="text-base font-semibold text-gray-900 mb-2" data-id="element-8143">
                  Dispute lost
                </div>
                <div className="text-sm text-gray-700 mb-4" data-id="element-8144">
                  The issuing bank has reviewed your evidence and ruled in favor
                  of the cardholder. The disputed amount will not be credited
                  back to your account.
                </div>
                <button onClick={handleViewChallengeDetails} className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 shadow-sm transition-colors" data-id="element-8145">
                  <FileTextIcon size={18} data-id="element-8146" />
                  View your submitted evidence
                </button>
              </div>
            </div>
          </div>}

        <div className="grid grid-cols-2 gap-8 mb-6" data-id="element-8147">
          <div data-id="element-8148">
            <div className="text-sm text-gray-500 mb-1" data-id="element-8149">Dispute amount</div>
            <div className="text-2xl font-semibold text-gray-900" data-id="element-8150">
              {formatCurrency(dispute.amount)}
            </div>
          </div>
          <div data-id="element-8151">
            <div className="text-sm text-gray-500 mb-1" data-id="element-8152">Response due</div>
            <div className="text-base font-medium text-gray-900" data-id="element-8153">
              {formatDateTime(dispute.dueDate)}
            </div>
            {hoursRemaining > 0 && hoursRemaining <= 24 && !isChallenged && <div className="text-sm text-red-600 font-medium" data-id="element-8154">
                Due in {hoursRemaining}{' '}
                {hoursRemaining === 1 ? 'hour' : 'hours'}
              </div>}
          </div>
        </div>

        {showAlert && effectiveStatus === 'requires_response' && !isChallenged && !isAccepted && <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg" data-id="element-8155">
              <div className="flex items-start justify-between" data-id="element-8156">
                <div className="flex items-start gap-3" data-id="element-8157">
                  <div className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-8158">
                    <InfoIcon size={14} className="text-white" data-id="element-8159" />
                  </div>
                  <div data-id="element-8160">
                    <div className="text-sm font-medium text-gray-900 mb-1" data-id="element-8161">
                      Challenge this dispute with customer's purchasing history.
                    </div>
                    <div className="text-sm text-gray-700" data-id="element-8162">
                      We have identified matching transactions that prove this
                      customer has a purchasing history with you (see{' '}
                      <button onClick={() => setActiveTab('related')} className="text-blue-600 hover:text-blue-700 underline" data-id="element-8163">
                        Related orders
                      </button>
                      ). You can challenge this dispute by providing additional
                      details about these matching transactions.
                    </div>
                  </div>
                </div>
                <button onClick={() => setShowAlert(false)} className="text-gray-400 hover:text-gray-600 flex-shrink-0" data-id="element-8164">
                  <CloseIcon size={18} data-id="element-8165" />
                </button>
              </div>
            </div>}

        {isChallenged && <div className="mb-6" data-id="element-8166">
            <button onClick={handleViewChallengeDetails} className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-8167">
              View challenge details
            </button>
          </div>}

        <div className="grid grid-cols-2 gap-x-8 gap-y-4 text-sm" data-id="element-8168">
          <div data-id="element-8169">
            <div className="text-gray-500 mb-1" data-id="element-8170">Reason</div>
            <div className="flex items-center gap-2" data-id="element-8171">
              <span className="font-medium text-gray-900" data-id="element-8172">
                {getReasonLabel(dispute.reason)}
              </span>
              <div className="relative inline-block" data-id="element-8173">
                <button onMouseEnter={() => setShowReasonTooltip(true)} onMouseLeave={() => setShowReasonTooltip(false)} className="inline-flex items-center" data-id="element-8174">
                  <InfoIcon size={14} className="text-gray-400 hover:text-gray-600" data-id="element-8175" />
                </button>
                {showReasonTooltip && <div className="absolute z-10 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg bottom-full left-0 mb-2" data-id="element-8176">
                    {getReasonDescription(dispute.reason)}
                    <div className="absolute bottom-0 left-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900" data-id="element-8177"></div>
                  </div>}
              </div>
            </div>
          </div>
          <div data-id="element-8178">
            <div className="text-gray-500 mb-1" data-id="element-8179">Network reason code</div>
            <div className="font-medium text-gray-900" data-id="element-8180">
              {dispute.networkReasonCode}
            </div>
          </div>
          <div data-id="element-8181">
            <div className="text-gray-500 mb-1" data-id="element-8182">Dispute stage</div>
            <div className="font-medium text-gray-900 capitalize" data-id="element-8183">
              {dispute.stage.replace(/_/g, ' ')}
            </div>
          </div>
          <div data-id="element-8184">
            <div className="text-gray-500 mb-1" data-id="element-8185">Dispute time</div>
            <div className="font-medium text-gray-900" data-id="element-8186">
              {formatDateTime(dispute.createdAt)}
            </div>
          </div>
          <div data-id="element-8187">
            <div className="text-gray-500 mb-1" data-id="element-8188">Transaction type</div>
            <div className="font-medium text-gray-900" data-id="element-8189">Payment</div>
          </div>
          <div data-id="element-8190">
            <div className="text-gray-500 mb-1" data-id="element-8191">Issuer comments</div>
            <div className="font-medium text-gray-900" data-id="element-8192">Recall</div>
          </div>
        </div>

        {dispute.status === 'requires_response' && !isChallenged && !isAccepted && <div className="flex items-center gap-3 mt-6 pt-6 border-t border-gray-200" data-id="element-8193">
              <button onClick={handleChallengeClick} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-600 hover:border-blue-700 rounded-md" data-id="element-8194">
                Challenge
              </button>
              <button onClick={handleAcceptClick} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md" data-id="element-8195">
                Accept
              </button>
            </div>}
      </div>

      {/* Tabs Section */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-8196">
        <div className="border-b border-gray-200" data-id="element-8197">
          <nav className="flex gap-8 px-6" aria-label="Tabs" data-id="element-8198">
            <button onClick={() => setActiveTab('payment')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'payment' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-8199">
              Payment summary
            </button>
            <button onClick={() => setActiveTab('related')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'related' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-8200">
              Related orders
            </button>
          </nav>
        </div>

        {/* Payment Summary Tab Content */}
        {activeTab === 'payment' && <div className="p-6" data-id="element-8201">
            <div className="space-y-4 text-sm" data-id="element-8202">
              <div data-id="element-8203">
                <div className="text-gray-500 mb-1" data-id="element-8204">Order ID</div>
                <div className="flex items-center gap-2" data-id="element-8205">
                  <span className="font-medium text-gray-900" data-id="element-8206">
                    {dispute.transactionId}
                  </span>
                  <div className="relative group" data-id="element-8207">
                    <button onClick={() => handleCopyOrderId(dispute.transactionId)} className="text-gray-400 hover:text-blue-600 transition-colors" title="Copy Order ID" data-id="element-8208">
                      {copiedOrderId === dispute.transactionId ? <CheckIcon size={16} className="text-green-600" data-id="element-8209" /> : <CopyIcon size={16} data-id="element-8210" />}
                    </button>
                    {copiedOrderId === dispute.transactionId && <div className="absolute left-1/2 -translate-x-1/2 -top-8 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap" data-id="element-8211">
                        Copied!
                      </div>}
                  </div>
                </div>
              </div>
              <div data-id="element-8212">
                <div className="text-gray-500 mb-1" data-id="element-8213">Source</div>
                <div className="flex items-center gap-2" data-id="element-8214">
                  {(() => {
                const {
                  name,
                  Icon
                } = getPaymentSource();
                return <>
                        <Icon size={16} className="text-gray-600" data-id="element-8215" />
                        <span className="font-medium text-gray-900" data-id="element-8216">
                          {name}
                        </span>
                      </>;
              })()}
                </div>
              </div>
              <div data-id="element-8217">
                <div className="text-gray-500 mb-1" data-id="element-8218">Payment type</div>
                <div className="font-medium text-gray-900" data-id="element-8219">Debit card</div>
              </div>
              <div data-id="element-8220">
                <div className="text-gray-500 mb-1" data-id="element-8221">Payment method</div>
                <div className="flex items-center gap-2" data-id="element-8222">
                  <span className="font-medium text-gray-900" data-id="element-8223">
                    {dispute.cardBrand} •••• {dispute.cardLast4}
                  </span>
                </div>
              </div>
              <div data-id="element-8224">
                <div className="text-gray-500 mb-1" data-id="element-8225">Cardholder name</div>
                <div className="font-medium text-gray-900" data-id="element-8226">
                  {dispute.customerName}
                </div>
              </div>
              <div data-id="element-8227">
                <div className="text-gray-500 mb-1" data-id="element-8228">Customer purchase IP</div>
                <div className="font-medium text-gray-900" data-id="element-8229">35.221.170.121</div>
              </div>
              <div data-id="element-8230">
                <div className="text-gray-500 mb-1" data-id="element-8231">Customer phone</div>
                <div className="font-medium text-gray-900" data-id="element-8232">+60 12 345 6789</div>
              </div>
              <div data-id="element-8233">
                <div className="text-gray-500 mb-1" data-id="element-8234">Security checks</div>
                <div className="space-y-1" data-id="element-8235">
                  <div className="flex items-center gap-2 text-green-600" data-id="element-8236">
                    <CheckCircleIcon size={16} data-id="element-8237" />
                    <span data-id="element-8238">Card security code</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-400" data-id="element-8239">
                    <XCircleIcon size={16} data-id="element-8240" />
                    <span data-id="element-8241">3DS</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-400" data-id="element-8242">
                    <XCircleIcon size={16} data-id="element-8243" />
                    <span data-id="element-8244">Billing address verification</span>
                  </div>
                </div>
              </div>
              <a href="#" className="text-blue-600 hover:text-blue-700 text-sm font-medium inline-block" data-id="element-8245">
                View payment details
              </a>
            </div>
          </div>}

        {/* Related Orders Tab Content */}
        {activeTab === 'related' && <div className="p-6" data-id="element-8246">
            <div className="mb-4 text-sm text-gray-600" data-id="element-8247">
              Customers may have a purchasing history with you, which could be
              used as evidence to challenge the claim that there is a fraudulent
              charge. To help you define this history, we have identified orders
              that qualify as{' '}
              <span className="font-medium" data-id="element-8248">
                matching transactions to the issuer
              </span>
              .{' '}
              <div className="relative inline-block" data-id="element-8249">
                <button onMouseEnter={() => setShowMatchingCriteriaTooltip(true)} onMouseLeave={() => setShowMatchingCriteriaTooltip(false)} className="inline-flex items-center" data-id="element-8250">
                  <InfoIcon size={14} className="text-gray-400 hover:text-gray-600" data-id="element-8251" />
                </button>
                {showMatchingCriteriaTooltip && <div className="absolute z-10 w-96 p-4 bg-gray-900 text-white text-xs rounded-lg shadow-lg bottom-full left-0 mb-2" data-id="element-8252">
                    <div className="space-y-2" data-id="element-8253">
                      <p data-id="element-8254">
                        To qualify as a matching transaction to the issuer, a
                        transaction must meet the following criteria:
                      </p>
                      <ul className="list-disc pl-4 space-y-1" data-id="element-8255">
                        <li data-id="element-8256">
                          At least two data elements (IP address, device ID /
                          fingerprint, shipping address, email address) must
                          match across all three transactions, and one of these
                          two must be the IP address or device ID or fingerprint
                        </li>
                        <li data-id="element-8257">
                          Between 120 and 365 days older than the date of the
                          disputed transaction
                        </li>
                        <li data-id="element-8258">
                          Undisputed, and not have been reported as fraudulent
                        </li>
                      </ul>
                      <p data-id="element-8259">
                        We also show you if the payment method matches, which
                        can be used to support your challenge.
                      </p>
                    </div>
                    <div className="absolute bottom-0 left-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900" data-id="element-8260"></div>
                  </div>}
              </div>
            </div>

            {/* Related Orders Table */}
            <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-8261">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-8262">
                <thead className="bg-gray-50" data-id="element-8263">
                  <tr data-id="element-8264">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8265">
                      Order ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8266">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8267">
                      Created Time
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8268">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8269">
                      Matching Criteria
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-8270">
                  {paginatedOrders.map(order => <tr key={order.id} className="hover:bg-gray-50" data-id="element-8271">
                      <td className="px-4 py-3" data-id="element-8272">
                        <div className="flex items-center gap-2" data-id="element-8273">
                          <span className="text-sm text-gray-900" data-id="element-8274">
                            {order.orderId}
                          </span>
                          <button className="text-blue-600 hover:text-blue-700" data-id="element-8275">
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-8276">
                              <path d="M13.3333 6H7.33333C6.59695 6 6 6.59695 6 7.33333V13.3333C6 14.0697 6.59695 14.6667 7.33333 14.6667H13.3333C14.0697 14.6667 14.6667 14.0697 14.6667 13.3333V7.33333C14.6667 6.59695 14.0697 6 13.3333 6Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-8277" />
                              <path d="M3.33333 10H2.66667C2.31304 10 1.97391 9.85952 1.72386 9.60947C1.47381 9.35942 1.33333 9.02029 1.33333 8.66667V2.66667C1.33333 2.31304 1.47381 1.97391 1.72386 1.72386C1.97391 1.47381 2.31304 1.33333 2.66667 1.33333H8.66667C9.02029 1.33333 9.35942 1.47381 9.60947 1.72386C9.85952 1.97391 10 2.31304 10 2.66667V3.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-8278" />
                            </svg>
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900" data-id="element-8279">
                        {formatCurrency(order.amount, order.currency)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900" data-id="element-8280">
                        {order.createdTime}
                      </td>
                      <td className="px-4 py-3" data-id="element-8281">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-8282">
                          {order.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right" data-id="element-8283">
                        <button onClick={() => setSelectedOrder(order)} className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-8284">
                          {order.matchingCriteria} of {order.totalCriteria}{' '}
                          criteria
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>

              {/* Pagination */}
              <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200" data-id="element-8285">
                <div className="flex items-center gap-2" data-id="element-8286">
                  <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-8287">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-8288">
                      <path d="M11 12L7 8L11 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-8289" />
                      <path d="M5 12L5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-8290" />
                    </svg>
                  </button>
                  <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-8291">
                    <ChevronLeftIcon size={16} data-id="element-8292" />
                  </button>
                  {Array.from({
                length: totalPages
              }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage(page)} className={`px-3 py-1 text-sm rounded ${currentPage === page ? 'bg-blue-600 text-white' : 'hover:bg-gray-200 text-gray-700'}`} data-id="element-8293">
                      {page}
                    </button>)}
                  <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-8294">
                    <ChevronRightIcon size={16} data-id="element-8295" />
                  </button>
                  <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-8296">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-8297">
                      <path d="M5 4L9 8L5 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-8298" />
                      <path d="M11 4L11 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-8299" />
                    </svg>
                  </button>
                </div>
                <div className="flex items-center gap-4" data-id="element-8300">
                  <div className="text-sm text-gray-700" data-id="element-8301">
                    Rows per page:{' '}
                    <span className="font-medium" data-id="element-8302">{rowsPerPage}</span>
                  </div>
                  <div className="text-sm text-gray-700" data-id="element-8303">
                    {(currentPage - 1) * rowsPerPage + 1} -{' '}
                    {Math.min(currentPage * rowsPerPage, relatedOrders.length)}{' '}
                    of {relatedOrders.length}
                  </div>
                </div>
              </div>
            </div>
          </div>}
      </div>

      {/* Payment Breakdown Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-8304">
        <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-8305">
          Payment breakdown
        </h3>
        <div className="space-y-3 max-w-md" data-id="element-8306">
          <div className="flex items-center justify-between" data-id="element-8307">
            <div className="text-sm text-gray-600" data-id="element-8308">Payment amount</div>
            <div className="text-sm font-medium text-gray-900" data-id="element-8309">
              {formatCurrency(paymentAmount)}
            </div>
          </div>
          <div className="flex items-center justify-between" data-id="element-8310">
            <div className="flex items-center gap-1" data-id="element-8311">
              <span className="text-sm text-gray-600" data-id="element-8312">
                Final disputed amount
              </span>
              <InfoIcon size={14} className="text-gray-400" data-id="element-8313" />
            </div>
            <div className="text-sm font-medium text-gray-900" data-id="element-8314">
              - {formatCurrency(disputedAmount)}
            </div>
          </div>
          <div className="flex items-center justify-between" data-id="element-8315">
            <div className="flex items-center gap-1" data-id="element-8316">
              <span className="text-sm text-gray-600" data-id="element-8317">Dispute fee</span>
              <InfoIcon size={14} className="text-gray-400" data-id="element-8318" />
            </div>
            <div className="text-sm font-medium text-gray-900" data-id="element-8319">
              - {formatCurrency(disputeFee)}
            </div>
          </div>
          <div className="pt-3 border-t border-gray-200" data-id="element-8320">
            <div className="flex items-center justify-between" data-id="element-8321">
              <div className="text-sm font-semibold text-gray-900" data-id="element-8322">
                Net amount
              </div>
              <div className="text-sm font-semibold text-gray-900" data-id="element-8323">
                - {formatCurrency(netAmount)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-8324">
        <div className="flex items-center justify-between mb-4" data-id="element-8325">
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-8326">
            Recent Activity
          </h3>
          <button onClick={() => setShowAddNote(!showAddNote)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-600 hover:border-blue-700 rounded-md transition-colors" data-id="element-8327">
            <PenIcon size={16} data-id="element-8328" />
            Add note
          </button>
        </div>

        {/* Add Note Form */}
        {showAddNote && <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-8329">
            <textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add a note for internal reference..." rows={3} className="w-full border border-gray-300 rounded-md p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-3" data-id="element-8330" />
            <div className="flex items-center justify-end gap-2" data-id="element-8331">
              <button onClick={() => {
            setShowAddNote(false);
            setNoteText('');
          }} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900" data-id="element-8332">
                Cancel
              </button>
              <button onClick={handleAddNote} disabled={!noteText.trim()} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-8333">
                Add note
              </button>
            </div>
          </div>}

        <div className="space-y-4" data-id="element-8334">
          {reversedLifecycle.map((event, index) => <div key={event.id} className="flex gap-4" data-id="element-8335">
              <div className="flex flex-col items-center" data-id="element-8336">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${event.status === 'note' ? 'bg-purple-100' : index === 0 ? 'bg-blue-100' : 'bg-gray-100'}`} data-id="element-8337">
                  {event.status === 'note' ? <FileTextIcon size={16} className="text-purple-600" data-id="element-8338" /> : <ClockIcon size={16} className={index === 0 ? 'text-blue-600' : 'text-gray-400'} data-id="element-8339" />}
                </div>
                {index < reversedLifecycle.length - 1 && <div className="w-0.5 h-12 bg-gray-200 my-1" data-id="element-8340"></div>}
              </div>
              <div className="flex-1 pb-4" data-id="element-8341">
                <div className="flex items-start justify-between gap-2" data-id="element-8342">
                  <div className="flex-1" data-id="element-8343">
                    {event.status === 'note' && <div className="flex items-center gap-2 mb-1" data-id="element-8344">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded" data-id="element-8345">
                          {event.isPinned && <PinIcon size={12} data-id="element-8346" />}
                          Internal Note
                        </span>
                      </div>}
                    {editingNoteId === event.id ? <div className="space-y-2" data-id="element-8347">
                        <textarea value={editingNoteText} onChange={e => setEditingNoteText(e.target.value)} rows={3} className="w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-8348" />
                        <div className="flex items-center gap-2" data-id="element-8349">
                          <button onClick={() => handleSaveEdit(event.id)} className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700" data-id="element-8350">
                            Save
                          </button>
                          <button onClick={handleCancelEdit} className="px-3 py-1 text-xs font-medium text-gray-700 hover:text-gray-900" data-id="element-8351">
                            Cancel
                          </button>
                        </div>
                      </div> : <>
                        <div className="text-sm font-medium text-gray-900" data-id="element-8352">
                          {event.description}
                        </div>
                        <div className="text-xs text-gray-500 mt-1" data-id="element-8353">
                          {formatDateTime(event.timestamp)}
                          {event.status === 'note' && event.author && <span data-id="element-8354"> by {event.author}</span>}
                        </div>
                      </>}
                  </div>
                  {event.status === 'note' && editingNoteId !== event.id && <div className="relative note-menu-container" data-id="element-8355">
                      <button onClick={() => setShowNoteMenu(showNoteMenu === event.id ? null : event.id)} className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100" data-id="element-8356">
                        <MoreVerticalIcon size={16} data-id="element-8357" />
                      </button>
                      {showNoteMenu === event.id && <div className="absolute right-0 top-8 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10" data-id="element-8358">
                          <button onClick={() => handlePinNote(event.id)} className="w-full px-4 py-2 text-left text-sm text-blue-600 hover:bg-gray-50 flex items-center gap-2" data-id="element-8359">
                            <PinIcon size={14} data-id="element-8360" />
                            {event.isPinned ? 'Unpin from top' : 'Pin to the top'}
                          </button>
                          <button onClick={() => handleEditNote(event.id)} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2" data-id="element-8361">
                            <PenIcon size={14} data-id="element-8362" />
                            Edit
                          </button>
                          <button onClick={() => handleDeleteNote(event.id)} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-50 flex items-center gap-2" data-id="element-8363">
                            <TrashIcon size={14} data-id="element-8364" />
                            Delete
                          </button>
                        </div>}
                    </div>}
                </div>
              </div>
            </div>)}
        </div>
      </div>

      {/* Challenge Modal */}
      {showChallengeModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-8365">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-8366">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowChallengeModal(false)} data-id="element-8367">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-8368"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-8369">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-8370">
              <div className="bg-white px-6 pt-6 pb-4" data-id="element-8371">
                <div className="flex items-start justify-between mb-4" data-id="element-8372">
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-8373">
                    You are challenging the dispute
                  </h2>
                  <button onClick={() => setShowChallengeModal(false)} className="text-gray-400 hover:text-gray-600" data-id="element-8374">
                    <CloseIcon size={20} data-id="element-8375" />
                  </button>
                </div>

                <p className="text-sm text-gray-600 mb-6" data-id="element-8376">
                  Here's a preview of what will happen next:
                </p>

                <div className="space-y-6" data-id="element-8377">
                  <div className="flex gap-4" data-id="element-8378">
                    <div className="flex flex-col items-center" data-id="element-8379">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-8380">
                        <PenIcon size={20} className="text-blue-600" data-id="element-8381" />
                      </div>
                      <div className="w-0.5 h-full bg-gray-200 mt-2" data-id="element-8382"></div>
                    </div>
                    <div className="flex-1 pb-6" data-id="element-8383">
                      <h3 className="text-sm font-semibold text-gray-900 mb-1" data-id="element-8384">
                        You submit the challenge
                      </h3>
                      <p className="text-sm text-gray-600" data-id="element-8385">
                        We'll guide you to provide the most compelling evidence
                        to support your challenge.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4" data-id="element-8386">
                    <div className="flex flex-col items-center" data-id="element-8387">
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0" data-id="element-8388">
                        <div className="w-3 h-3 rounded-full border-2 border-gray-400" data-id="element-8389"></div>
                      </div>
                    </div>
                    <div className="flex-1" data-id="element-8390">
                      <h3 className="text-sm font-semibold text-gray-900 mb-1" data-id="element-8391">
                        Issuer reviews the evidence & decides on next steps
                      </h3>
                      <p className="text-sm text-gray-600 mb-2" data-id="element-8392">
                        If the issuer is convinced by the submitted evidence,
                        the disputed amount will be credited back to you with
                        the next settlement batch. Please note that the
                        chargeback fee will not be credited to you.
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-8393">
                        If they are not convinced by the submitted evidence,
                        they will decline the evidence and the dispute will be
                        lost. No further action is possible.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3" data-id="element-8394">
                <button onClick={() => setShowChallengeModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900" data-id="element-8395">
                  Cancel
                </button>
                <button onClick={handleChallengeConfirm} className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" data-id="element-8396">
                  Challenge
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Evidence Form Modal - keeping existing */}
      {showEvidenceForm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-8397">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-8398">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowEvidenceForm(false)} data-id="element-8399">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-8400"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-8401">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full" data-id="element-8402">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-8403">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4" data-id="element-8404">
                  Submit Evidence
                </h3>
                <div className="space-y-4" data-id="element-8405">
                  <div data-id="element-8406">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8407">
                      Customer Communication
                    </label>
                    <textarea rows={3} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Describe any communication with the customer..." data-id="element-8408" />
                  </div>
                  <div data-id="element-8409">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8410">
                      Shipping/Delivery Documentation
                    </label>
                    <textarea rows={2} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Tracking numbers, delivery confirmation, etc..." data-id="element-8411" />
                  </div>
                  <div data-id="element-8412">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-8413">
                      Upload Supporting Documents
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center" data-id="element-8414">
                      <UploadIcon size={32} className="mx-auto text-gray-400 mb-2" data-id="element-8415" />
                      <div className="text-sm text-gray-600" data-id="element-8416">
                        Drag and drop files here, or click to browse
                      </div>
                      <div className="text-xs text-gray-500 mt-1" data-id="element-8417">
                        PDF, JPG, PNG up to 10MB each
                      </div>
                      <input type="file" multiple className="hidden" data-id="element-8418" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-8419">
                <button type="button" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm" onClick={() => setShowEvidenceForm(false)} data-id="element-8420">
                  Submit Evidence
                </button>
                <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" onClick={() => setShowEvidenceForm(false)} data-id="element-8421">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Matching Criteria Side Panel */}
      {selectedOrder && <div className="fixed inset-0 z-50 overflow-hidden" data-id="element-8422">
          <div className="absolute inset-0 overflow-hidden" data-id="element-8423">
            <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setSelectedOrder(null)} data-id="element-8424"></div>
            <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex" data-id="element-8425">
              <div className="w-screen max-w-md" data-id="element-8426">
                <div className="h-full flex flex-col bg-white shadow-xl overflow-y-auto" data-id="element-8427">
                  {/* Header */}
                  <div className="px-6 py-4 border-b border-gray-200" data-id="element-8428">
                    <div className="flex items-center justify-between" data-id="element-8429">
                      <h2 className="text-lg font-semibold text-gray-900" data-id="element-8430">
                        Related Order
                      </h2>
                      <button onClick={() => setSelectedOrder(null)} className="text-gray-400 hover:text-gray-600" data-id="element-8431">
                        <CloseIcon size={20} data-id="element-8432" />
                      </button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 px-6 py-4" data-id="element-8433">
                    {/* Order Status */}
                    <div className="mb-6" data-id="element-8434">
                      <div className="text-sm font-medium text-gray-900 mb-2" data-id="element-8435">
                        Order
                      </div>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 uppercase" data-id="element-8436">
                        {selectedOrder.status}
                      </span>
                    </div>

                    {/* Order ID */}
                    <div className="mb-6" data-id="element-8437">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-8438">Order ID</div>
                      <div className="flex items-center gap-2" data-id="element-8439">
                        <span className="text-sm font-medium text-gray-900" data-id="element-8440">
                          {selectedOrder.orderId}
                        </span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-8441">
                          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-8442">
                            <path d="M13.3333 6H7.33333C6.59695 6 6 6.59695 6 7.33333V13.3333C6 14.0697 6.59695 14.6667 7.33333 14.6667H13.3333C14.0697 14.6667 14.6667 14.0697 14.6667 13.3333V7.33333C14.6667 6.59695 14.0697 6 13.3333 6Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-8443" />
                            <path d="M3.33333 10H2.66667C2.31304 10 1.97391 9.85952 1.72386 9.60947C1.47381 9.35942 1.33333 9.02029 1.33333 8.66667V2.66667C1.33333 2.31304 1.47381 1.97391 1.72386 1.72386C1.97391 1.47381 2.31304 1.33333 2.66667 1.33333H8.66667C9.02029 1.33333 9.35942 1.47381 9.60947 1.72386C9.85952 1.97391 10 2.31304 10 2.66667V3.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-8444" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Matching Criteria */}
                    <div className="mb-6" data-id="element-8445">
                      <div className="flex items-center gap-2 mb-3" data-id="element-8446">
                        <h3 className="text-sm font-semibold text-gray-900" data-id="element-8447">
                          Matching criteria
                        </h3>
                        <InfoIcon size={14} className="text-gray-400" data-id="element-8448" />
                      </div>

                      <div className="space-y-6" data-id="element-8449">
                        {matchingCriteriaDetails.map((category, idx) => <div key={idx} data-id="element-8450">
                            <div className="text-sm font-medium text-gray-900 mb-1" data-id="element-8451">
                              {category.category}
                            </div>
                            {category.subtitle && <div className="text-xs text-gray-500 mb-2" data-id="element-8452">
                                {category.subtitle}
                              </div>}
                            <div className="space-y-2" data-id="element-8453">
                              {category.items.map((item, itemIdx) => <div key={itemIdx} className="flex items-center gap-2" data-id="element-8454">
                                  {item.matched ? <CheckCircleIcon size={16} className="text-green-600" data-id="element-8455" /> : <XCircleIcon size={16} className="text-red-600" data-id="element-8456" />}
                                  <span className="text-sm text-gray-700" data-id="element-8457">
                                    {item.label}
                                  </span>
                                </div>)}
                            </div>
                          </div>)}
                      </div>
                    </div>

                    {/* Payment Summary */}
                    <div className="border-t border-gray-200 pt-6" data-id="element-8458">
                      <h3 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-8459">
                        Payment summary
                      </h3>
                      <div className="space-y-3 text-sm" data-id="element-8460">
                        <div className="flex justify-between" data-id="element-8461">
                          <span className="text-gray-600" data-id="element-8462">
                            Payment created time
                          </span>
                          <span className="text-gray-900" data-id="element-8463">
                            2025-07-06
                            <br data-id="element-8464" />
                            09:01 UTC+0800
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-8465">
                          <span className="text-gray-600" data-id="element-8466">Payment amount</span>
                          <span className="text-gray-900" data-id="element-8467">
                            {formatCurrency(selectedOrder.amount, selectedOrder.currency)}
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-8468">
                          <span className="text-gray-600" data-id="element-8469">Card type</span>
                          <span className="text-gray-900" data-id="element-8470">CREDIT</span>
                        </div>
                        <div className="flex justify-between" data-id="element-8471">
                          <span className="text-gray-600" data-id="element-8472">Payment method</span>
                          <div className="flex items-center gap-2" data-id="element-8473">
                            <span className="text-blue-600 font-medium" data-id="element-8474">
                              visa
                            </span>
                            <span className="text-gray-900" data-id="element-8475">
                              Visa •••• 9400
                            </span>
                          </div>
                        </div>
                        <div className="flex justify-between" data-id="element-8476">
                          <span className="text-gray-600" data-id="element-8477">Cardholder name</span>
                          <span className="text-gray-900" data-id="element-8478">Auto Testing</span>
                        </div>
                        <div data-id="element-8479">
                          <div className="text-gray-600 mb-2" data-id="element-8480">
                            Payment security check
                          </div>
                          <div className="space-y-1" data-id="element-8481">
                            <div className="flex items-center gap-2" data-id="element-8482">
                              <CheckCircleIcon size={14} className="text-green-600" data-id="element-8483" />
                              <span className="text-gray-700 text-xs" data-id="element-8484">
                                Card security code
                              </span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-8485">
                              <CheckCircleIcon size={14} className="text-green-600" data-id="element-8486" />
                              <span className="text-gray-700 text-xs" data-id="element-8487">3DS</span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-8488">
                              <XCircleIcon size={14} className="text-gray-400" data-id="element-8489" />
                              <span className="text-gray-700 text-xs" data-id="element-8490">
                                Billing address verification
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between" data-id="element-8491">
                    <button onClick={() => setSelectedOrder(null)} className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-8492">
                      Close
                    </button>
                    <button className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-8493">
                      View payment details
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}

      {/* Challenge Details Modal */}
      {showChallengeDetails && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-8494">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-8495">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowChallengeDetails(false)} data-id="element-8496">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-8497"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-8498">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full" data-id="element-8499">
              <div className="bg-white px-8 pt-8 pb-6" data-id="element-8500">
                <div className="flex items-start justify-between mb-6" data-id="element-8501">
                  <h2 className="text-2xl font-bold text-gray-900" data-id="element-8502">
                    Challenge details
                  </h2>
                  <button onClick={() => setShowChallengeDetails(false)} className="text-gray-400 hover:text-gray-600" data-id="element-8503">
                    <CloseIcon size={24} data-id="element-8504" />
                  </button>
                </div>

                <div className="space-y-6" data-id="element-8505">
                  <div data-id="element-8506">
                    <h3 className="text-base font-semibold text-gray-900 mb-4" data-id="element-8507">
                      Challenge details
                    </h3>
                    <div className="space-y-3" data-id="element-8508">
                      <div className="flex" data-id="element-8509">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-8510">
                          Reason for challenge
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-8511">
                          The customer has a purchasing history with me
                        </span>
                      </div>
                      <div className="flex" data-id="element-8512">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-8513">
                          Type of product or service
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-8514">
                          Physical product
                        </span>
                      </div>
                      <div className="flex" data-id="element-8515">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-8516">
                          Supporting evidence
                        </span>
                        <div className="flex-1" data-id="element-8517">
                          <div className="flex items-center gap-3" data-id="element-8518">
                            <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center flex-shrink-0" data-id="element-8519">
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-8520">
                                <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-8521" />
                                <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-8522" />
                              </svg>
                            </div>
                            <div className="flex-1" data-id="element-8523">
                              <div className="text-sm font-medium text-gray-900" data-id="element-8524">
                                OHMM-Cairo-Specs-She...9Jul25.pdf
                              </div>
                              <div className="text-sm text-gray-500" data-id="element-8525">
                                1.04 MB
                              </div>
                            </div>
                            <button className="text-blue-600 hover:text-blue-700" data-id="element-8526">
                              <EyeIcon size={18} data-id="element-8527" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div data-id="element-8528">
                    <h3 className="text-base font-semibold text-gray-900 mb-4" data-id="element-8529">
                      Transaction details
                    </h3>
                    <div className="space-y-3" data-id="element-8530">
                      <div className="flex" data-id="element-8531">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-8532">
                          Product details
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-8533">
                          IPHONE7 * 5, {formatCurrency(dispute.amount)}
                        </span>
                      </div>
                      <div className="flex" data-id="element-8534">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-8535">
                          Shipping address
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-8536">
                          Rabbit Gateway Office, 123 Silom Road, Bang Rak,
                          Bangkok 10500, Thailand
                        </span>
                      </div>
                      <div className="flex" data-id="element-8537">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-8538">
                          Customer name
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-8539">Test name</span>
                      </div>
                      <div className="flex" data-id="element-8540">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-8541">
                          Customer email
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-8542">
                          customer@example.com
                        </span>
                      </div>
                      <div className="flex" data-id="element-8543">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-8544">
                          Customer purchase IP
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-8545">
                          35.221.170.121
                        </span>
                      </div>
                      <div className="flex" data-id="element-8546">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-8547">
                          Customer billing address
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-8548">
                          Rabbit Gateway Office, 123 Silom Road, Bang Rak,
                          Bangkok 10500, Thailand
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-8 py-4 flex justify-end" data-id="element-8549">
                <button onClick={() => setShowChallengeDetails(false)} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-8550">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Help Modal */}
      {showHelpModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-8551">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-8552">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowHelpModal(false)} data-id="element-8553">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-8554"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-8555">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full" data-id="element-8556">
              <div className="bg-white px-8 pt-8 pb-6" data-id="element-8557">
                <div className="flex items-start justify-between mb-6" data-id="element-8558">
                  <h2 className="text-2xl font-bold text-gray-900" data-id="element-8559">
                    {isChallenged ? 'What happens after a dispute is challenged?' : 'How to respond to a dispute?'}
                  </h2>
                  <button onClick={() => setShowHelpModal(false)} className="text-gray-400 hover:text-gray-600" data-id="element-8560">
                    <CloseIcon size={24} data-id="element-8561" />
                  </button>
                </div>

                {isChallenged ? <div className="space-y-6" data-id="element-8562">
                    <div className="flex gap-4" data-id="element-8563">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold" data-id="element-8564">
                        1
                      </div>
                      <div className="flex-1" data-id="element-8565">
                        <p className="text-gray-700" data-id="element-8566">
                          The disputed amount will be credited back to your
                          wallet in the next settlement batch. You can view the
                          settlement details in the "Dispute settlement" section
                          of the payment details page.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4" data-id="element-8567">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold" data-id="element-8568">
                        2
                      </div>
                      <div className="flex-1" data-id="element-8569">
                        <p className="text-gray-700" data-id="element-8570">
                          If you agree that the dispute reason is valid, it is
                          best to accept the dispute.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4" data-id="element-8571">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold" data-id="element-8572">
                        3
                      </div>
                      <div className="flex-1" data-id="element-8573">
                        <p className="text-gray-700" data-id="element-8574">
                          If the customer agrees to withdraw the dispute, you
                          still need to challenge the dispute and submit the
                          agreement as supporting evidence.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4" data-id="element-8575">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold" data-id="element-8576">
                        4
                      </div>
                      <div className="flex-1" data-id="element-8577">
                        <p className="text-gray-700" data-id="element-8578">
                          If the customer is upholding the dispute but you
                          believe it is not valid, consider whether you have
                          compelling evidence to challenge the dispute with.
                        </p>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-200" data-id="element-8579">
                      <p className="text-sm text-gray-600" data-id="element-8580">
                        Note that challenging the dispute can lead to higher
                        fees if the issuer is not convinced by your evidence.
                      </p>
                    </div>
                  </div> : <div className="space-y-6" data-id="element-8581">
                    <div className="flex gap-4" data-id="element-8582">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold" data-id="element-8583">
                        1
                      </div>
                      <div className="flex-1" data-id="element-8584">
                        <p className="text-gray-700" data-id="element-8585">
                          Review the dispute details and if necessary contact
                          your customer to understand why they filed the
                          dispute.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4" data-id="element-8586">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold" data-id="element-8587">
                        2
                      </div>
                      <div className="flex-1" data-id="element-8588">
                        <p className="text-gray-700" data-id="element-8589">
                          If you agree that the dispute reason is valid, it is
                          best to accept the dispute.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4" data-id="element-8590">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold" data-id="element-8591">
                        3
                      </div>
                      <div className="flex-1" data-id="element-8592">
                        <p className="text-gray-700" data-id="element-8593">
                          If the customer agrees to withdraw the dispute, you
                          still need to challenge the dispute and submit the
                          agreement as supporting evidence.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4" data-id="element-8594">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold" data-id="element-8595">
                        4
                      </div>
                      <div className="flex-1" data-id="element-8596">
                        <p className="text-gray-700" data-id="element-8597">
                          If the customer is upholding the dispute but you
                          believe it is not valid, consider whether you have
                          compelling evidence to challenge the dispute with.
                        </p>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-200" data-id="element-8598">
                      <p className="text-sm text-gray-600" data-id="element-8599">
                        Note that challenging the dispute can lead to higher
                        fees if the issuer is not convinced by your evidence.
                      </p>
                    </div>
                  </div>}
              </div>

              <div className="bg-gray-50 px-8 py-4 flex justify-end" data-id="element-8600">
                <button onClick={() => setShowHelpModal(false)} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-8601">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Accept Dispute Modal */}
      {showAcceptModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-8602">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-8603">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowAcceptModal(false)} data-id="element-8604">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-8605"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-8606">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-8607">
              <div className="bg-white px-6 pt-6 pb-4" data-id="element-8608">
                <div className="flex items-start gap-4" data-id="element-8609">
                  <div className="flex-shrink-0" data-id="element-8610">
                    <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center" data-id="element-8611">
                      <AlertTriangleIcon size={24} className="text-orange-600" data-id="element-8612" />
                    </div>
                  </div>
                  <div className="flex-1" data-id="element-8613">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-8614">
                      Accept this dispute?
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-8615">
                      Accepting this dispute is final. Once you accept, the
                      customer will receive a refund of{' '}
                      {formatCurrency(dispute.amount)}. The funding timelines
                      can vary by the issuer.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3" data-id="element-8616">
                <button onClick={() => setShowAcceptModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900" data-id="element-8617">
                  Cancel
                </button>
                <button onClick={handleAcceptConfirm} className="px-6 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700" data-id="element-8618">
                  Accept dispute
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Add Note Modal */}
      {showAddNote && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-8619">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-8620">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowAddNote(false)} data-id="element-8621">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-8622"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-8623">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full" data-id="element-8624">
              <div className="bg-white px-6 pt-6 pb-4" data-id="element-8625">
                <div className="flex items-start justify-between mb-4" data-id="element-8626">
                  <h2 className="text-lg font-semibold text-gray-900" data-id="element-8627">
                    Add Note
                  </h2>
                  <button onClick={() => setShowAddNote(false)} className="text-gray-400 hover:text-gray-600" data-id="element-8628">
                    <CloseIcon size={20} data-id="element-8629" />
                  </button>
                </div>

                <div className="space-y-4" data-id="element-8630">
                  <div data-id="element-8631">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8632">
                      Note
                    </label>
                    <textarea value={noteText} onChange={e => setNoteText(e.target.value)} rows={4} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Add a note for internal reference..." data-id="element-8633" />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4" data-id="element-8634">
                  <button onClick={() => setShowAddNote(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900" data-id="element-8635">
                    Cancel
                  </button>
                  <button onClick={handleAddNote} disabled={!noteText.trim()} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-8636">
                    Add Note
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}